import { formatCurrency, formatPercent } from "../../../../../../../utils";
import { DISABLED_AMOUNT_TYPES } from "../compsConstants";

import type { RawCommissionAmount } from "../types";
import type {
  CompsTableCommissionType,
  CompsTableCoverageStatus,
  CompsTableHeapedAmount,
  CompsTableProposalStatus,
} from "@platform/api";

const isHeapedAmount = (
  value: RawCommissionAmount,
): value is CompsTableHeapedAmount =>
  value !== null &&
  typeof value === "object" &&
  "first" in value &&
  "thereafter" in value;

const formatDollar = (value: number): string =>
  formatCurrency(value, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });

const formatHeaped = (amount: RawCommissionAmount): string => {
  if (!isHeapedAmount(amount)) return "Heaped";
  const { first, thereafter } = amount;
  // Storage has no null slot for "not entered" — first/thereafter are
  // required numbers, so an unset side is stored as 0 (see
  // HeapedCommissionEditor/EditCommissionEditor's `getValue`).
  const hasFirst = first > 0;
  const hasThereafter = thereafter > 0;

  if (hasFirst && hasThereafter) {
    return `Heaped ${formatPercent(first)} year 1 / ${formatPercent(thereafter)} year 2`;
  }
  if (hasFirst) return `${formatPercent(first)} year 1`;
  if (hasThereafter) return `${formatPercent(thereafter)} year 2`;
  return "Heaped";
};

const SIMPLE_LABELS: Partial<Record<CompsTableCommissionType, string>> = {
  standard: "Carrier Standard",
  net: "Net",
  notDisclosed: "Not Disclosed",
  notApplicable: "N/A",
  notRequested: "N/A",
  notSupported: "N/A",
  notQuoted: "N/A",
  dtq: "DTQ",
};

/**
 * Maps a commission's type + amount to the single display string shown in the
 * Comps grid — the mapping this ticket moves off the export side and into the
 * UI (see the mapping table in CD-2245).
 */
export const formatCommissionDisplay = (
  cell:
    | {
        commissionType?: CompsTableCommissionType | null;
        amount?: RawCommissionAmount;
      }
    | undefined,
  proposalStatus?: CompsTableProposalStatus,
  coverageStatus?: CompsTableCoverageStatus | null,
): string => {
  if (!cell) return "-";
  if (proposalStatus === "pending") return "-";
  if (proposalStatus === "received" && coverageStatus === "declinedToQuote")
    return "DTQ";

  const { commissionType, amount } = cell;
  if (!commissionType) return "-";

  if (commissionType in SIMPLE_LABELS) {
    return SIMPLE_LABELS[commissionType] as string;
  }

  switch (commissionType) {
    case "heaped":
      return formatHeaped(amount ?? null);
    case "percentage":
      return typeof amount === "number"
        ? `Flat ${formatPercent(amount)}`
        : "Flat";
    case "graded":
      return typeof amount === "number"
        ? `Graded ${formatPercent(amount)}`
        : "Graded";
    case "pepm":
      return typeof amount === "number"
        ? `${formatDollar(amount)} PEPM`
        : "PEPM";
    default:
      return "-";
  }
};

/** Whether the Amount field should be disabled in the Edit Commission modal for this type. */
export const isCommissionAmountDisabled = (
  commissionType: CompsTableCommissionType | null,
): boolean =>
  !commissionType ||
  DISABLED_AMOUNT_TYPES.includes(
    commissionType as (typeof DISABLED_AMOUNT_TYPES)[number],
  );
